import React from "react";
import {
  ArrowDown,
  MessageCircle,
  Mail,
  Smartphone,
  Sparkles,
} from "lucide-react";
import PhoneMockup from "./PhoneMockup";

const Hero = () => {
  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative min-h-screen overflow-hidden bg-cream pt-32 md:pt-40"
    >
      <div className="pointer-events-none absolute inset-0 bg-grid opacity-60" />

      <div className="pointer-events-none absolute -right-32 top-40 h-[420px] w-[420px] rounded-full bg-acid/70 blur-[120px]" />

      <div className="pointer-events-none absolute -left-40 bottom-0 h-[320px] w-[320px] rounded-full bg-ultra/30 blur-[120px]" />

      <div className="relative mx-auto grid max-w-[1480px] grid-cols-12 gap-6 px-6 md:px-12">
        <div className="col-span-12 reveal-up">
          <div className="flex items-center gap-3 font-mono text-xs uppercase tracking-[0.22em] text-ultra">
            <span className="blink-cursor inline-block h-1.5 w-1.5 rounded-full bg-ultra" />
            Android app studio · est. 2022 · made for craft
          </div>
        </div>

        <div
          className="col-span-12 reveal-up lg:col-span-8"
          style={{ animationDelay: "120ms" }}
        >
          <h1 className="font-display text-[14vw] font-extrabold uppercase leading-[0.86] tracking-tightest text-ink md:text-[10.5vw] lg:text-[8.5vw]">
            We engineer
            <br />

            <span className="inline-flex items-baseline gap-2 md:gap-4">
              <span className="relative inline-block">
                android

                <span
                  className="absolute -bottom-2 left-0 -z-0 h-3 w-full bg-acid"
                  aria-hidden
                />
              </span>

              <Smartphone
                className="hidden h-[7vw] w-[7vw] -rotate-12 text-ultra md:inline-block"
                strokeWidth={1.6}
              />
            </span>

            <br />

            <span className="text-ink/30">apps people</span>

            <br />

            <span className="italic text-ultra">
              actually open.
            </span>
          </h1>
        </div>

        <div
          className="col-span-12 flex items-end justify-center reveal-up lg:col-span-4 lg:justify-end"
          style={{ animationDelay: "260ms" }}
        >
          <div className="relative float-y">
            <PhoneMockup>
              <div className="flex h-full w-full flex-col bg-cream">
                <div className="flex items-center justify-between px-4 pt-6 font-mono text-[10px] text-ink/70">
                  <span>9:41</span>
                  <span>● ●●</span>
                </div>

                <div className="px-4 pt-4">
                  <div className="font-mono text-[9px] uppercase tracking-widest text-ultra">
                    today
                  </div>

                  <div className="font-display text-2xl font-extrabold leading-tight text-ink">
                    Ship beautiful builds.
                  </div>
                </div>

                <div className="mx-4 mt-4 rounded-md border border-ink/10 bg-white p-3">
                  <div className="flex items-center gap-2">
                    <div className="h-6 w-6 rounded-sm bg-ultra" />

                    <div className="flex-1">
                      <div className="h-2 w-3/4 rounded-sm bg-ink/80" />

                      <div className="mt-1 h-1.5 w-1/2 rounded-sm bg-ink/20" />
                    </div>
                  </div>

                  <div className="mt-3 h-12 rounded-sm bg-acid" />
                </div>

                <div className="mx-4 mt-3 grid grid-cols-2 gap-2">
                  <div className="rounded-md border border-ink/10 bg-white p-2">
                    <div className="h-1.5 w-1/2 bg-ink/30" />

                    <div className="mt-2 font-display text-lg font-extrabold text-ink">
                      87%
                    </div>
                  </div>

                  <div className="rounded-md bg-ink p-2 text-cream">
                    <div className="h-1.5 w-1/2 bg-cream/40" />

                    <div className="mt-2 font-display text-lg font-extrabold">
                      12k
                    </div>
                  </div>
                </div>

                <div className="mt-auto px-4 pb-6 pt-4">
                  <div className="flex items-center justify-between rounded-full border border-ink bg-cream px-3 py-2">
                    <span className="font-mono text-[9px] uppercase">
                      home
                    </span>

                    <span className="font-mono text-[9px] uppercase text-ink/40">
                      stats
                    </span>

                    <span className="font-mono text-[9px] uppercase text-ink/40">
                      me
                    </span>
                  </div>
                </div>
              </div>
            </PhoneMockup>

            <div className="absolute -left-10 top-8 hidden items-center gap-2 rounded-sm border border-ink bg-acid px-3 py-2 font-mono text-[10px] uppercase tracking-widest shadow-brutal md:flex">
              <Sparkles className="h-3 w-3" />
              kotlin · compose
            </div>

            <div className="absolute -right-6 bottom-16 hidden rounded-sm border border-ink bg-white px-3 py-2 font-mono text-[10px] uppercase tracking-widest shadow-brutal md:block">
              4.9★ play store
            </div>
          </div>
        </div>

        <div
          className="col-span-12 mt-12 reveal-up lg:col-span-7 lg:mt-20"
          style={{ animationDelay: "380ms" }}
        >
          <p className="max-w-xl font-body text-lg leading-relaxed text-ink/70 md:text-xl">
            Rexomi is a tiny studio obsessed with native Android. We design,
            engineer and launch products that feel impossibly smooth — from
            zero-to-one MVPs to scaled apps that millions touch every day.
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-4">
            <a
              href="mailto:hello@rxo.me?subject=Inquiry%20for%20Android%20App%20Development&body=Hi%20Rexomi%2C%0A%0AI%E2%80%99m%20looking%20for%20Android%20app%20development%20services%20and%20would%20love%20to%20discuss%20my%20requirements.%0A%0AMy%20Name%3A%0AMy%20Phone%20Number%3A%0AMy%20Country%3A%0A%0AMy%20Company%20%2F%20Brand%20Name%3A%0AServices%20Interested%20In%3A%20Android%20App%20Development%0AApp%20Category%20I%20Am%20Interested%20In%3A%0A%0AAdditional%20Notes%3A"
              data-testid="hero-email-cta"
              className="group inline-flex items-center gap-3 rounded-sm border border-ink bg-ink px-6 py-4 font-mono text-xs uppercase tracking-[0.18em] text-cream transition-all hover:bg-ultra"
            >
              <Mail className="h-4 w-4" />
              mail us
            </a>

            <a
              href="https://wa.me/918697878785?text=Hi%20Rexomi%2C%0A%0AI%E2%80%99m%20looking%20for%20Android%20app%20development%20services%20and%20would%20love%20to%20discuss%20my%20requirements.%0A%0AMy%20Name%3A%0AMy%20Company%20%2F%20Brand%20Name%3A%0AApp%20Category%20I%20Am%20Interested%20In%3A%0AProject%20Details%3A"
              target="_blank"
              rel="noreferrer"
              data-testid="hero-whatsapp-cta"
              className="group inline-flex items-center gap-3 rounded-sm border border-ink bg-cream px-6 py-4 font-mono text-xs uppercase tracking-[0.18em] text-ink transition-all hover:bg-acid"
            >
              <MessageCircle className="h-4 w-4" />
              whatsapp us
            </a>
          </div>
        </div>

        <div
          className="col-span-12 mt-12 reveal-up lg:col-span-5 lg:mt-20"
          style={{ animationDelay: "500ms" }}
        >
          <div className="grid grid-cols-3 gap-6 border-t border-ink/15 pt-8">
            {[
              { v: "40+", l: "Apps shipped" },
              { v: "7M+", l: "Downloads" },
              { v: "4.8★", l: "Avg. rating" },
            ].map((s) => (
              <div key={s.l}>
                <div className="font-display text-4xl font-extrabold tracking-tightest text-ink">
                  {s.v}
                </div>

                <div className="mt-1 font-mono text-[10px] uppercase tracking-widest text-ink/50">
                  {s.l}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="relative mt-20 flex items-center justify-center pb-10 font-mono text-[10px] uppercase tracking-[0.3em] text-ink/50">
        <ArrowDown className="mr-2 h-3 w-3 animate-bounce" />
        scroll for more
      </div>
    </section>
  );
};

export default Hero;